import React from "react";
import {
  StyleSheet,
  View,
  Text,
  type StyleProp,
  type ViewStyle,
  type TextStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedToolTip } from "./ThemedToolTip";
import { Fonts } from "@/constants/ThemeColours";

import { useComponentTheme } from "@/hooks/useComponentTheme";
export {
  type PlatformSymbolType,
  type IPlatformSymbolConfig,
  PLATFORM_SYMBOLS,
} from "@/constants/themes/components/symbol-theme";
import {
  type PlatformSymbolType,
  type IPlatformSymbolConfig,
  PLATFORM_SYMBOLS,
} from "@/constants/themes/components/symbol-theme";

/**
 * Props for ThemedPlatformSymbol.
 */
export interface IThemedPlatformSymbolProps {
  /** The platform symbol identifier */
  type: PlatformSymbolType;
  /** Size variant (default: "small") */
  size?: "small" | "medium" | "large";
  /** Whether to render the accompanying text label beside the icon */
  showLabel?: boolean;
  /** Custom label override when showLabel is true */
  label?: string;
  /** Custom tooltip override */
  tooltip?: string;
  /** Whether to suppress the tooltip */
  hideTooltip?: boolean;
  /** Tooltip popover position (default: "top") */
  tooltipPosition?: "top" | "bottom" | "left" | "right";
  /** Container style override */
  style?: StyleProp<ViewStyle>;
  /** Text style override when showLabel is true */
  textStyle?: StyleProp<TextStyle>;
  /** Test identifier */
  testID?: string;
}

/**
 * ThemedPlatformSymbol: Universal standardized status symbol with tooltip.
 *
 * Implements the Platform Symbols standard:
 * - Owner: Gold Key (#D4AF37) with tooltip "Owner"
 * - Five Star: Gold Star (#EAB308) with tooltip "5 stars"
 * - Saved: Cloud with check (#10B981) with tooltip "Saved"
 * - Shared: Collaborators (#0284C7) with tooltip "Shared with you"
 * - Heirloom: Archive Box (#B45309) with tooltip "Physical Heirloom"
 * - Read-Only: Lock (#64748B) with tooltip "Read-only"
 * - Synced: Cloud Download (#0EA5E9) with tooltip "Available Offline"
 */
export function ThemedPlatformSymbol({
  type,
  size = "small",
  showLabel = false,
  label,
  tooltip,
  hideTooltip = false,
  tooltipPosition = "top",
  style,
  textStyle,
  testID,
}: IThemedPlatformSymbolProps): React.JSX.Element {
  const { symbol: symbolTheme } = useComponentTheme();
  const config = symbolTheme[type] || symbolTheme.owner || PLATFORM_SYMBOLS[type];
  const resolvedTooltip = tooltip ?? config.tooltip;
  const resolvedLabel = label ?? config.defaultLabel;

  const iconSize = size === "large" ? 16 : size === "medium" ? 13 : 11;

  const content = (
    <View
      testID={testID || `platform-symbol-${type}`}
      accessibilityRole="image"
      accessibilityLabel={resolvedTooltip}
      style={[
        styles.badge,
        size === "small" && styles.badgeSmall,
        size === "medium" && styles.badgeMedium,
        size === "large" && styles.badgeLarge,
        showLabel && styles.badgeWithLabel,
        {
          backgroundColor: config.backgroundColor,
          borderColor: config.borderColor,
        },
        style,
      ]}
    >
      <Ionicons
        name={config.icon}
        size={iconSize}
        color={config.color}
      />
      {showLabel ? (
        <Text
          numberOfLines={1}
          style={[
            styles.labelText,
            size === "small" && styles.labelTextSmall,
            size === "medium" && styles.labelTextMedium,
            size === "large" && styles.labelTextLarge,
            { color: config.color },
            textStyle,
          ]}
        >
          {resolvedLabel}
        </Text>
      ) : null}
    </View>
  );

  if (hideTooltip) {
    return content;
  }

  return (
    <ThemedToolTip text={resolvedTooltip} position={tooltipPosition} style={{ overflow: "visible" }}>
      {content}
    </ThemedToolTip>
  );
}

const styles = StyleSheet.create({
  badge: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderRadius: 3,
  },
  badgeSmall: {
    minWidth: 18,
    height: 18,
    paddingHorizontal: 3,
    gap: 3,
  },
  badgeMedium: {
    minWidth: 22,
    height: 22,
    paddingHorizontal: 4,
    gap: 4,
  },
  badgeLarge: {
    minWidth: 26,
    height: 26,
    paddingHorizontal: 6,
    gap: 5,
  },
  badgeWithLabel: {
    paddingHorizontal: 6,
  },
  labelText: {
    fontWeight: "600",
    fontFamily: Fonts.mono,
  },
  labelTextSmall: {
    fontSize: 10,
    lineHeight: 12,
  },
  labelTextMedium: {
    fontSize: 11,
    lineHeight: 14,
  },
  labelTextLarge: {
    fontSize: 12,
    lineHeight: 16,
  },
});

export default ThemedPlatformSymbol;
